/**
 * EditScheduleModal - 수업 정보 수정(reschedule) 모달 (폼 3)
 *
 * 시간/날짜·강사·제목·정원을 수정한다. 예약 회원(bookings)은 변경하지 않으며
 * (회원 이동은 예약취소+재예약), type은 기존 예약자의 수강권 검증 때문에 수정 불가(읽기 전용).
 * 변경된 필드만 백엔드 `updateSchedule`로 전송한다.
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";
import { useUpdateSchedule } from "@/hooks/useCenterSchedules";
import type { UpdateScheduleParams } from "@/hooks/useCenterSchedules";

interface EditScheduleModalProps {
  isOpen: boolean;
  centerId: number;
  schedule: StudiomateScheduleItem;
  onClose: () => void;
  onUpdated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

/** datetime-local 초기값: "YYYY-MM-DDTHH:mm" */
function toLocalDateTime(date: string, time: string): string {
  return `${date}T${time}`;
}

export function EditScheduleModal({
  isOpen,
  centerId,
  schedule,
  onClose,
  onUpdated,
}: EditScheduleModalProps) {
  const initialStart = toLocalDateTime(schedule.date, schedule.startTime);
  const initialEnd = toLocalDateTime(schedule.date, schedule.endTime);

  const [title, setTitle] = useState(schedule.title);
  const [startOn, setStartOn] = useState(initialStart);
  const [endOn, setEndOn] = useState(initialEnd);
  const [staffName, setStaffName] = useState(schedule.staffName);
  const [maxTraineeInput, setMaxTraineeInput] = useState(String(schedule.maxTrainees));
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateSchedule = useUpdateSchedule();

  useEffect(() => {
    if (isOpen) {
      setTitle(schedule.title);
      setStartOn(initialStart);
      setEndOn(initialEnd);
      setStaffName(schedule.staffName);
      setMaxTraineeInput(String(schedule.maxTrainees));
      setValidationError(null);
      updateSchedule.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, schedule]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError("수업명을 입력해주세요.");
      return;
    }
    const trimmedStaff = staffName.trim();
    if (!trimmedStaff) {
      setValidationError("강사명을 입력해주세요.");
      return;
    }
    if (!startOn || !endOn) {
      setValidationError("시작/종료 시각을 입력해주세요.");
      return;
    }
    if (endOn <= startOn) {
      setValidationError("종료 시각은 시작 시각보다 뒤여야 합니다.");
      return;
    }
    const maxTrainee = Number(maxTraineeInput);
    if (!Number.isInteger(maxTrainee) || maxTrainee < 1) {
      setValidationError("최대 인원은 1 이상의 정수로 입력해주세요.");
      return;
    }

    const params: UpdateScheduleParams = { centerId, scheduleId: schedule.id };
    if (trimmedTitle !== schedule.title) params.title = trimmedTitle;
    if (startOn !== initialStart) params.startOn = startOn;
    if (endOn !== initialEnd) params.endOn = endOn;
    if (trimmedStaff !== schedule.staffName) params.staffName = trimmedStaff;
    if (maxTrainee !== schedule.maxTrainees) params.maxTrainee = maxTrainee;

    const changed = Object.keys(params).filter((k) => k !== "centerId" && k !== "scheduleId");
    if (changed.length === 0) {
      setValidationError("변경된 내용이 없습니다.");
      return;
    }

    setValidationError(null);
    updateSchedule.mutate(params, { onSuccess: () => onUpdated() });
  };

  const serverErrorMessage = updateSchedule.isError ? getErrorMessage(updateSchedule.error) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수업 수정</h2>
        <p className="text-sm text-gray-500 mb-4">
          유형: {schedule.type === "G" ? "그룹" : "개인"} (변경 불가) · 예약 회원은 유지됩니다.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="editScheduleTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              수업명 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleStart"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              시작 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleStart"
              type="datetime-local"
              value={startOn}
              onChange={(e) => setStartOn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleEnd"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              종료 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleEnd"
              type="datetime-local"
              value={endOn}
              onChange={(e) => setEndOn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleStaff"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              강사 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleStaff"
              type="text"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleMax"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              최대 인원 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleMax"
              type="number"
              step={1}
              min={1}
              value={maxTraineeInput}
              onChange={(e) => setMaxTraineeInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}

          {updateSchedule.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "수업 수정에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={updateSchedule.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateSchedule.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
